import { stripHtml } from '@/helpers/estimation';

export function htmlToPlainText(html: string): string {
  if (!html) return '';

  const doc = new DOMParser().parseFromString(html, 'text/html');

  function processNode(
    node: Node,
    depth = 0,
    isOrderedList = false,
    listIndex: number[] = [],
  ): string {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.nodeValue?.trim() ?? '';
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return '';

    const element = node as HTMLElement;

    switch (element.tagName.toLowerCase()) {
      case 'br':
        return '\n';
      case 'h1':
      case 'h2':
      case 'h3':
      case 'h4':
      case 'h5':
      case 'h6':
      case 'p':
      case 'div':
        return processChildren(node, depth, isOrderedList, listIndex) + '\n';
      case 'li': {
        let prefix = '-';
        // quill does it this way: https://github.com/slab/quill/issues/3014#issuecomment-2492501253
        const quillListType = element.getAttribute('data-list');
        if (isOrderedList && quillListType !== 'bullet') {
          listIndex[depth] = (listIndex[depth] || 0) + 1;
          prefix = `${listIndex[depth]}.`;
        }
        return `${' '.repeat(depth * 2)}${prefix} ${processChildren(
          node,
          depth + 1, // Increase depth for children
          isOrderedList,
          listIndex,
        )}\n`;
      }
      case 'ul':
        return '\n' + processChildren(node, depth + 1, false, listIndex);
      case 'ol':
        return '\n' + processChildren(node, depth + 1, true, [...listIndex, 0]); // Reset index for nested ordered lists
      default:
        return processChildren(node, depth, isOrderedList, listIndex);
    }
  }

  function processChildren(
    node: Node,
    depth: number,
    isOrderedList: boolean,
    listIndex: number[],
  ): string {
    return Array.from(node.childNodes)
      .map((child) => processNode(child, depth, isOrderedList, listIndex))
      .join('');
  }

  return stripHtml(processNode(doc.body).trim());
}
